import React, { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCopy } from '@tabler/icons-react';
import config from 'config';
import { useAuth } from 'context/auth-context';
import useClassTypes from 'hooks/classType/useClassTypes';
import useGym from 'hooks/gym/useGym';
import useUpdateGym from 'hooks/gym/useUpdateGym';
import useSalesPortalConfiguration from 'hooks/salesPortalConfiguration/useSalesPortalConfiguration';
import notificationHandler from 'utils/notifications';

interface EmbedCalendarSettingsModalProps {
  opened: boolean;
  onClose: () => void;
}

interface CalendarWidgetSettings {
  calendarWidgetEnabled: boolean;
  allowPurchasesViaTheCalendarWidget: boolean;
}

const defaultcalendarWidgetSettings: CalendarWidgetSettings = {
  calendarWidgetEnabled: false,
  allowPurchasesViaTheCalendarWidget: false,
};

const EmbedCalendarSettingsModal = ({ opened, onClose }: EmbedCalendarSettingsModalProps): JSX.Element => {
  const { session } = useAuth();
  const { data: gymData, isLoading: gymLoading } = useGym(session?.gym?.gymId);
  const { data: salesPortalData } = useSalesPortalConfiguration(gymData?.data.salesPortalConfigurationId);
  const { data: classTypesData } = useClassTypes({ ordering: 'name' });

  const gym = gymData?.data;
  const salesPortalSlug = salesPortalData?.data?.slug || '';
  const salesPortalEnabled = salesPortalData?.data?.enabled ?? false;

  const [settings, setSettings] = useState<CalendarWidgetSettings>(defaultcalendarWidgetSettings);
  const [selectedClassTypeId, setSelectedClassTypeId] = useState<string | null>(null);
  const [defaultDarkMode, setDefaultDarkMode] = useState(false);
  const updateMutation = useUpdateGym();
  const { successNotification, errorNotification } = notificationHandler();

  const classTypeOptions = useMemo(() => {
    if (!classTypesData?.pages) return [];
    return classTypesData.pages.flatMap(
      page =>
        page.data.results?.map(ct => ({
          value: ct.kiloClassTypeId,
          label: ct.name,
        })) ?? [],
    );
  }, [classTypesData]);

  React.useEffect(() => {
    if (gym?.calendarWidgetSettings) {
      setSettings({
        calendarWidgetEnabled: gym.calendarWidgetSettings.calendarWidgetEnabled ?? false,
        allowPurchasesViaTheCalendarWidget:
          gym.calendarWidgetSettings.allowPurchasesViaTheCalendarWidget ?? false,
      });
    }
  }, [gym]);

  const widgetUrl = config.CALENDAR_WIDGET_CDN_URL;

  // Encode ID using base64 URL-safe encoding
  const encodeId = (id: string): string => {
    const base64 = btoa(id);
    // Convert to URL-safe base64
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const embedCode = useMemo(() => {
    if (!gym || !salesPortalSlug) return '';

    const baseUrl = window.location.origin;
    const encodedGymId = encodeId(gym.gymId);
    const classTypeAttr = selectedClassTypeId
      ? `\n     data-default-class-type-id="${encodeId(selectedClassTypeId)}"`
      : '';
    const darkModeAttr = defaultDarkMode ? '\n     data-dark-mode="true"' : '';

    return `<!-- Kilo Calendar Widget -->
<div id="kilo-calendar-widget"
     data-gym-id="${encodedGymId}"
     data-base-url="${baseUrl}"
     data-sales-portal-slug="${salesPortalSlug}"${classTypeAttr}${darkModeAttr}>
</div>
<script src="${widgetUrl}"></script>`;
  }, [gym, salesPortalSlug, widgetUrl, selectedClassTypeId, defaultDarkMode]);

  const updateSettings = (newSettings: CalendarWidgetSettings, successMessage: string): void => {
    if (!gym) return;

    const previousSettings = { ...settings };
    setSettings(newSettings);

    const request = {
      ...gym,
      gymMailConfig: null,
      calendarWidgetSettings: {
        calendarWidgetEnabled: newSettings.calendarWidgetEnabled,
        allowPurchasesViaTheCalendarWidget: newSettings.allowPurchasesViaTheCalendarWidget,
      },
    };

    updateMutation.mutate(request, {
      onSuccess: () => {
        successNotification('Calendar widget settings updated', successMessage);
      },
      onError: error => {
        setSettings(previousSettings); // Revert on error
        errorNotification('Failed to update settings', error.response?.data.message);
      },
    });
  };

  const handleWidgetToggle = (checked: boolean): void => {
    const newSettings: CalendarWidgetSettings = {
      ...settings,
      calendarWidgetEnabled: checked,
    };
    updateSettings(
      newSettings,
      checked ? 'Public calendar embedding is now enabled.' : 'Public calendar embedding is now disabled.',
    );
  };

  const handlePurchasesToggle = (checked: boolean): void => {
    const newSettings: CalendarWidgetSettings = {
      ...settings,
      allowPurchasesViaTheCalendarWidget: checked,
    };
    updateSettings(
      newSettings,
      checked
        ? 'Purchases via calendar widget are now enabled.'
        : 'Purchases via calendar widget are now disabled.',
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={3}>Embed Calendar Widget</Title>}
      size='lg'
      centered
    >
      <Stack spacing='lg'>
        <Text c='dimmed'>
          Allow visitors to view your class schedule on your website by embedding the calendar widget. When
          enabled, visitors can see available classes and be directed to sign up or log in.
        </Text>

        <Switch
          label='Enable public calendar embedding'
          description='When enabled, your class schedule can be embedded on external websites'
          checked={settings.calendarWidgetEnabled}
          onChange={e => handleWidgetToggle(e.currentTarget.checked)}
          disabled={gymLoading || updateMutation.isLoading}
          size='md'
        />

        {settings.calendarWidgetEnabled && (
          <>
            <Switch
              label='Allow purchases via calendar widget'
              description='When enabled, visitors can sign up and purchase packages directly from the calendar widget'
              checked={settings.allowPurchasesViaTheCalendarWidget && salesPortalEnabled}
              onChange={e => handlePurchasesToggle(e.currentTarget.checked)}
              disabled={gymLoading || updateMutation.isLoading || !salesPortalEnabled}
              size='md'
            />

            {!salesPortalEnabled && (
              <Alert icon={<IconAlertCircle size={16} />} color='yellow'>
                Sales Portal must be enabled to allow purchases via the calendar widget. Please enable it in
                your Sales Portal settings.
              </Alert>
            )}

            <Select
              label='Default class type filter'
              description='Optionally pre-select a class type to filter the calendar by default'
              placeholder='All classes'
              data={classTypeOptions}
              value={selectedClassTypeId}
              onChange={setSelectedClassTypeId}
              clearable
              searchable
            />

            <Switch
              label='Default to dark mode'
              description='When enabled, the calendar widget will start in dark mode (users can still toggle)'
              checked={defaultDarkMode}
              onChange={e => setDefaultDarkMode(e.currentTarget.checked)}
              size='md'
            />

            {(selectedClassTypeId || defaultDarkMode) && (
              <Alert icon={<IconAlertCircle size={16} />} color='blue' variant='light'>
                If you&apos;ve already embedded the calendar, you&apos;ll need to replace the old embed code
                with the new one below to apply these settings.
              </Alert>
            )}

            <Stack spacing='xs'>
              <Text fw={500}>Embed Code</Text>
              <Text size='sm' c='dimmed'>
                Copy and paste this code into your website where you want the calendar to appear.
              </Text>
              <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {embedCode}
              </Code>
              <CopyButton value={embedCode}>
                {({ copied, copy }) => (
                  <Button
                    color={copied ? 'teal' : 'blue'}
                    onClick={copy}
                    leftIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    variant='light'
                  >
                    {copied ? 'Copied!' : 'Copy Embed Code'}
                  </Button>
                )}
              </CopyButton>
            </Stack>

            <Stack spacing='xs'>
              <Text fw={500}>How to Embed</Text>
              <Text size='sm' c='dimmed'>
                1. Copy the embed code above
              </Text>
              <Text size='sm' c='dimmed'>
                2. Paste it into your website&apos;s HTML where you want the calendar to appear
              </Text>
              <Text size='sm' c='dimmed'>
                3. The widget will automatically load and display your class schedule
              </Text>
            </Stack>
          </>
        )}

        <Group position='right' mt='md'>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EmbedCalendarSettingsModal;
